/**
 * Generated by scripts/design-system/regenerate-design-system.cjs.
 * Source spec hash: 119b712454200e9d6d738c0e8d2b04f064362ab76b9bd2008bf714441908f7f4
 */
import cn from 'classnames'
import React, { ComponentPropsWithoutRef, ReactElement } from 'react'

import style from './Pill.style'

type PillRefinedOwnProps = {
  className?: string
  children?: React.ReactNode
  color?: 'highlight' | 'new' | 'severe' | 'pending' | 'saas'
  disabled?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  label?: string
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  presentation?: 'default' | 'callout' | 'inline-status'
  size?: 'small' | 'default' | 'medium' | 'large'
  tone?: 'muted' | 'severe' | 'highlight' | 'cool' | 'ai' | 'warning'
  type?: 'default' | 'highlight' | 'severe' | 'pending' | 'saas' | 'aiNew'
}

export type PillRefinedProps = PillRefinedOwnProps & Omit<ComponentPropsWithoutRef<'span'>, keyof PillRefinedOwnProps>

type PillLegacyOwnProps = {
  className?: string
  disabled?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  size?: 'small' | 'medium' | 'large'
  type?: 'default' | 'highlight' | 'severe' | 'pending' | 'saas' | 'aiNew'
}

export type PillProps = PillLegacyOwnProps & Omit<ComponentPropsWithoutRef<'span'>, keyof PillLegacyOwnProps>

type PillPresentation = NonNullable<PillRefinedOwnProps['presentation']>
type PillTone = NonNullable<PillRefinedOwnProps['tone']>

function resolveTone(
  presentation: PillPresentation,
  tone?: PillTone,
  type?: PillRefinedOwnProps['type'],
  color?: PillRefinedOwnProps['color'],
): PillTone {
  if (presentation === 'inline-status') {
    return 'muted'
  }

  if (tone) {
    return tone
  }

  switch (type ?? color) {
    case 'highlight':
      return 'highlight'
    case 'severe':
      return 'severe'
    case 'pending':
      return 'warning'
    case 'saas':
      return 'cool'
    case 'aiNew':
    case 'new':
      return 'ai'
    case 'default':
    default:
      return 'muted'
  }
}

type PillComponent = {
  (props: PillProps): ReactElement
  (props: PillRefinedProps): ReactElement
}

const Pill: PillComponent = (
  props: PillProps | PillRefinedProps,
): ReactElement => {
  const normalizedProps = props as PillRefinedProps
  const {
    className = '',
    color,
    disabled = false,
    iconLeft,
    iconRight,
    label,
    onClick,
    presentation,
    size,
    tone,
    type,
    children,
    ...rest
  } = normalizedProps
  const resolvedPresentation = presentation ?? (size === 'large' ? 'callout' : 'default')
  const resolvedSize = resolvedPresentation === 'default' && size === 'small' ? 'small' : 'default'
  const resolvedTone = resolveTone(resolvedPresentation, tone, type, color)
  const isSmall = resolvedSize === 'small'
  const legacySizeClass = resolvedPresentation === 'callout' ? 'large' : isSmall ? 'small' : 'medium'
  const legacyTypeClass = type ?? (color === 'new' ? 'aiNew' : color) ?? 'default'
  const isClickable = Boolean(onClick && resolvedPresentation !== 'inline-status' && !isSmall)
  const classes = cn(
    'Pill',
    className,
    legacySizeClass,
    legacyTypeClass,
    `presentation-${resolvedPresentation}`,
    `tone-${resolvedTone}`,
    `size-${resolvedSize}`,
    { clickable: isClickable },
  )
  const contents = (
    <>
      {!isSmall && iconLeft ? <span aria-hidden={true} className='leadingIcon'>{iconLeft}</span> : null}
      <span className='label'>{label ?? children}</span>
      {!isSmall && iconRight ? <span aria-hidden={true} className='trailingIcon'>{iconRight}</span> : null}
    </>
  )

  if (isClickable) {
    return (
      <button
        {...(rest as ComponentPropsWithoutRef<'button'>)}
        className={classes}
        css={style}
        disabled={disabled}
        onClick={onClick}
        type='button'
      >
        {contents}
      </button>
    )
  }

  return (
    <span {...rest} className={classes} css={style}>
      {contents}
    </span>
  )
}

export default Pill
